'use client';

import { AiProficiencyReportCard } from '@/domains/pages/ai-proficiency/components/ai-proficiency-report-card';
import { PromotionPotentialSection } from '@/domains/pages/ai-proficiency/components/promotion-potential-section';
import { UpskillingChartLegend } from '@/domains/pages/ai-proficiency/components/upskilling-legend';
import { CreateProgramModal, type CreateProgramModalContext } from '@/domains/pages/ai-proficiency/components/create-program-modal';
import { UpskillingDetailsDrawer } from '@/domains/pages/ai-proficiency/components/upskilling-details-drawer';
import { UpskillingLevelCard } from '@/domains/pages/ai-proficiency/components/upskilling-level-card';
import { UpskillingScopeBar } from '@/domains/pages/ai-proficiency/components/upskilling-scope-bar';
import { UPSKILLING_LEVEL_GRID_ORDER, type AifiLevel } from '@/domains/pages/ai-proficiency/constants';
import { buildNoSegmentsEmptyReason } from '@/domains/pages/ai-proficiency/utils/segment-empty-state';
import { UpskillingChartsLoadingSkeleton } from '@/domains/pages/ai-proficiency/upskilling-page-loading';
import { useUpskillingPageData, type UpskillingView } from '@/domains/pages/ai-proficiency/hooks/use-upskilling-page-data';
import { useAiProficiencyShellLoading } from '@/domains/pages/ai-proficiency/hooks/use-ai-proficiency-shell-loading';
import { shouldHideNebulyPageBody, useSyncNebulyPageViewState } from '@/domains/pages/hooks/use-sync-nebuly-page-view-state';
import { useNebulyPageTitleSubject } from '@/domains/pages/hooks/use-nebuly-page-title-subject';
import type { NebulyPageBodyProps } from '@/domains/pages/types';
import { useCreateQueryStringUrl } from '@/hooks/use-create-query-string-url';
import { useProjectsContext } from '@/providers/projects-provider';
import { useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useMemo, useState } from 'react';
function parseUpskillingView(value: string | null): UpskillingView {
  if (value === 'team' || value === 'market' || value === 'tenure') return value;
  return 'all';
}
export function UpskillingPageBody({
  pageKey,
  timeRange: _timeRange
}: NebulyPageBodyProps) {
  const {
    projectId
  } = useProjectsContext();
  const searchParams = useSearchParams();
  const router = useRouter();
  const createQueryStringUrl = useCreateQueryStringUrl();
  const view = parseUpskillingView(searchParams.get('view'));
  const segmentFromUrl = searchParams.get('segment') ?? undefined;
  const data = useUpskillingPageData({
    projectId,
    view,
    selectedSegmentName: segmentFromUrl
  });
  useNebulyPageTitleSubject(view !== 'all' && data.selectedSegmentName ? data.formatSegmentName(data.selectedSegmentName) : undefined);
  const [detailsLevel, setDetailsLevel] = useState<AifiLevel | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [programContext, setProgramContext] = useState<CreateProgramModalContext | null>(null);
  const [programOpen, setProgramOpen] = useState(false);
  const levelMap = useMemo(() => new Map(data.levels.map(level => [level.level, level])), [data.levels]);
  const scopeLabel = view === 'all' ? 'org' : data.segmentNoun;
  const promotionScopeDescription = useMemo(() => {
    if (view === 'all') {
      return 'Users within 5 points of the next level. Training these people first moves the mix fastest.';
    }
    return `Users in ${data.formatSegmentName(data.selectedSegmentName)} within 5 points of the next level. Training these people first moves the team's mix fastest.`;
  }, [data.formatSegmentName, data.selectedSegmentName, view]);
  useEffect(() => {
    if (view === 'all' || data.segments.length === 0 || segmentFromUrl) return;
    const defaultName = data.segments[0]?.name;
    if (!defaultName) return;
    router.replace(createQueryStringUrl({
      params: {
        segment: defaultName
      }
    }));
  }, [createQueryStringUrl, data.segments, router, segmentFromUrl, view]);
  const handleViewChange = useCallback((nextView: UpskillingView) => {
    if (nextView === 'all') {
      router.replace(createQueryStringUrl({
        params: {
          view: 'all'
        },
        toRemoveParams: ['segment']
      }));
      return;
    }
    router.replace(createQueryStringUrl({
      params: {
        view: nextView
      },
      toRemoveParams: ['segment']
    }));
  }, [createQueryStringUrl, router]);
  const handleSelectSegment = useCallback((name: string) => {
    router.replace(createQueryStringUrl({
      params: {
        segment: name
      }
    }));
  }, [createQueryStringUrl, router]);
  const handleOpenDetails = useCallback((level_0: AifiLevel) => {
    UPSKILLING_LEVEL_GRID_ORDER.forEach(item => {
      document.getElementById(`upskilling-radar-tooltip-${item}`)?.remove();
    });
    setDetailsLevel(level_0);
    setDetailsOpen(true);
  }, []);
  const handleCreateProgram = useCallback((level_1: AifiLevel, dimensionKey: string, dimensionLabel: string) => {
    setProgramContext({
      level: level_1,
      dimensionKey,
      dimensionLabel
    });
    setProgramOpen(true);
  }, []);
  const isNoSegmentsEmpty = !data.isInitialLoading && !data.isChartsLoading && !data.isError && view !== 'all' && data.segments.length === 0;
  const isGenericEmpty = !data.isInitialLoading && !data.isChartsLoading && !data.isError && data.totalUsers === 0;
  const showPageShellLoading = useAiProficiencyShellLoading(pageKey, data.isInitialLoading, data.isError);
  useSyncNebulyPageViewState({
    isLoading: showPageShellLoading,
    isError: data.isError,
    error: data.error,
    refetch: data.refetch,
    isEmpty: isNoSegmentsEmpty || isGenericEmpty,
    emptyReason: isNoSegmentsEmpty ? buildNoSegmentsEmptyReason(view) : undefined
  });
  if (showPageShellLoading || data.isError) {
    return null;
  }
  if (isNoSegmentsEmpty) {
    return <div data-testid={`nebuly-page-body-${pageKey}`}>
        <UpskillingScopeBar view={view} onViewChange={handleViewChange} segments={data.segments} selectedSegmentName={data.selectedSegmentName} onSelectSegment={handleSelectSegment} />
      </div>;
  }
  if (shouldHideNebulyPageBody(false, false, isGenericEmpty)) {
    return null;
  }
  const detailsSnapshot = detailsLevel ? levelMap.get(detailsLevel) : undefined;
  const detailsGroupDimensions = detailsLevel ? data.levelMethodology[detailsLevel] ?? [] : [];
  return <div className="border-b border-b-lightborder" data-testid={`nebuly-page-body-${pageKey}`}>
      <UpskillingScopeBar view={view} onViewChange={handleViewChange} segments={data.segments} selectedSegmentName={data.selectedSegmentName} onSelectSegment={handleSelectSegment} />

      {data.isInitialLoading || data.isChartsLoading ? <UpskillingChartsLoadingSkeleton /> : <>
          <div className="border-b border-b-lightborder px-8 py-3">
            <UpskillingChartLegend />
          </div>

          <div className="grid grid-cols-4 items-start border-b border-b-lightborder">
            <div className="col-span-4">
              <AiProficiencyReportCard title="How to upskill each group" contentClassName="p-0">
                <div className="grid grid-cols-2 divide-x divide-y divide-lightborder">
                  {UPSKILLING_LEVEL_GRID_ORDER.map(level_2 => {
                const groupDimensions = data.levelMethodology[level_2] ?? [];
                return <UpskillingLevelCard key={level_2} level={level_2} snapshot={levelMap.get(level_2)} groupDimensions={groupDimensions} orgDimensions={data.orgMethodologyDimensions ?? []} levelTargets={data.levelTargets[level_2]} scopeLabel={scopeLabel} suppressRadarTooltip={detailsOpen && detailsLevel === level_2} onViewDetails={() => handleOpenDetails(level_2)} onCreateProgram={(dimensionKey_0, dimensionLabel_0) => handleCreateProgram(level_2, dimensionKey_0, dimensionLabel_0)} />;
              })}
                </div>
              </AiProficiencyReportCard>
            </div>
          </div>

          <div className="grid grid-cols-4 items-start">
            <div className="col-span-4">
              <AiProficiencyReportCard title="Promotion potential" contentClassName="p-0">
                <PromotionPotentialSection transitions={data.promotionTransitions} totalCloseToPromotion={data.totalCloseToPromotion} scopeDescription={promotionScopeDescription} />
              </AiProficiencyReportCard>
            </div>
          </div>
        </>}

      <UpskillingDetailsDrawer open={detailsOpen} onOpenChange={setDetailsOpen} level={detailsLevel} snapshot={detailsSnapshot} groupDimensions={detailsGroupDimensions} orgDimensions={data.orgMethodologyDimensions ?? []} levelTargets={detailsLevel ? data.levelTargets[detailsLevel] : {}} scopeLabel={scopeLabel} onCreateProgram={(dimensionKey_1, dimensionLabel_1) => {
      if (!detailsLevel) return;
      handleCreateProgram(detailsLevel, dimensionKey_1, dimensionLabel_1);
    }} />

      <CreateProgramModal open={programOpen} onOpenChange={setProgramOpen} context={programContext} />
    </div>;
}